import { clone } from "ramda";
import type { StoreApi } from "zustand";

import type { WorkflowEditorStore } from "@/modules/workflow/stores/workflow-editor-store-types.ts";
import type { WorkflowParameter } from "@/modules/workflow/utils/types.ts";

export const undo = (
  set: StoreApi<WorkflowEditorStore>["setState"],
  get: StoreApi<WorkflowEditorStore>["getState"],
) => {
  if (get().timelineIndex <= 0 || get().timeline.length <= 1) {
    return;
  }
  const { nodes, edges, parameters } = get().timeline[get().timelineIndex - 1];
  const newTimelineIndex = get().timelineIndex - 1;

  const oldNodes = clone(get().nodes);
  // FIXME: newParameters never queried. We already have a story for that
  const newParameters: WorkflowParameter[] = [];

  // Copy the parameters that are not in the current nodes to applied them AFTER the nodes are restored
  if (Array.isArray(parameters)) {
    for (const parameter of parameters) {
      if (!oldNodes?.map((node) => node.id).includes(parameter.nodeId)) {
        newParameters.push(parameter);
      }
    }
  }

  const newNodes = nodes.map((node) => {
    const oldNode = oldNodes?.find((n) => n.id === node.id);
    const nodeStatus = oldNode?.data.status;
    return { ...node, data: { ...node.data, status: nodeStatus } };
  });

  const deletedNodes = oldNodes?.filter((oldNode) => !newNodes?.some((newNode) => newNode.id === oldNode.id));
  const addedNodes = newNodes.filter((newNode) => !oldNodes?.some((oldNode) => oldNode.id === newNode.id));

  // Wipe deleted nodes and reset activity errors
  if (deletedNodes?.length > 0) {
    for (const deletedNode of deletedNodes) {
      void get().resetActivityNodeErrors(deletedNode.id);
    }
    get().wipeTemporaryData(deletedNodes?.map((node) => node.id));
  }

  set({
    nodes: newNodes,
    edges: edges,
    timelineIndex: newTimelineIndex,
    canUndo: newTimelineIndex > 0,
    canRedo: newTimelineIndex < get().timeline.length - 1,
  });

  for (const addedNode of addedNodes) {
    const parameter = parameters.find((parameter) => parameter.nodeId === addedNode.id);
    if (parameter) {
      const { nodeId, parameterId, values, staged } = parameter;
      get().setActivityParameterValue(nodeId, parameterId, values, true);
      get().setActivityParameterStaged(nodeId, parameterId, staged);
      get().checkConnections(nodeId);
    } else {
      get().registerActivityParameters(addedNode.id);
      get().setActivityComputing(addedNode.id);
    }
  }
  void get().cleanAllParameters();
};
